import { inject, Injectable } from '@angular/core';
import { ApiService } from '@/app/services/api.service';
import * as config from '@/lib-backend/methods/config';

/** A brand-kit image: raw base64 for the AI service + a data URL for `<img>`. */
export interface BrandKitImage {
    b64: string;
    contentType: string;
    dataUrl: string;
}

/** Resolved brand-kit, ready to seed the AI dialogs and to preview. */
export interface BrandKit {
    templateId: string | null;
    version: string | null;
    style: Record<string, unknown> | null;
    ogBasePlate: BrandKitImage | null;
    favicon: BrandKitImage | null;
    references: BrandKitImage[];
}

interface RawManifest {
    templateId?: unknown;
    version?: unknown;
    styleUrl?: unknown;
    ogBasePlateUrl?: unknown;
    faviconUrl?: unknown;
    brandIconReferenceUrls?: unknown;
}

interface RawFile {
    b64: string;
    contentType: string;
}

/**
 * Resolves a template's brand-kit through the Admin API's `getSiteTemplateFile`
 * (same-origin `/api`, so no CORS): the realm's assigned SiteTemplate version
 * ships a `meta/brand-kit.json` index pointing at the style descriptor, OG base
 * plate, favicon, and brand-icon references. Files come back base64; image
 * bytes are forwarded to the AI service inline (it accepts base64) and exposed
 * as data URLs for preview, so nothing needs to be publicly hosted.
 *
 * `getSiteTemplateFile` / `getSiteTemplate` are global-admin only and need the
 * realm to have a SiteTemplate assigned with a published version. Any failure
 * (no template, no manifest, not global admin, parse error) is swallowed and
 * returns null, leaving the dialogs' manual fields in place.
 */
@Injectable({ providedIn: 'root' })
export class BrandKitService {
    private readonly api = inject(ApiService);

    /** Cache per (templateId, version) so navigating between realms doesn't refetch. */
    private readonly cache = new Map<string, BrandKit | null>();

    /** `siteTemplateId` is `RealmConfig.SiteTemplateId`; null when none is assigned. */
    async resolve(siteTemplateId: string | null): Promise<BrandKit | null> {
        if (!siteTemplateId) return null;

        const version = await this.currentVersion(siteTemplateId);
        if (!version) return null;

        const cacheKey = `${siteTemplateId}@${version}`;
        if (this.cache.has(cacheKey)) return this.cache.get(cacheKey)!;

        const kit = await this.fetchKit(siteTemplateId, version);
        this.cache.set(cacheKey, kit);
        return kit;
    }

    /** Drop a cached entry (e.g. after publishing a new template version). */
    invalidate(siteTemplateId: string, version: string): void {
        this.cache.delete(`${siteTemplateId}@${version}`);
    }

    private async currentVersion(siteTemplateId: string): Promise<string | null> {
        try {
            const resp = await this.api.client.call(
                config.getSiteTemplate({ SiteTemplateId: siteTemplateId })
            );
            return resp.Result === 'Ok' ? resp.SiteTemplate.CurrentVersion : null;
        } catch {
            return null;
        }
    }

    private async fetchKit(siteTemplateId: string, version: string): Promise<BrandKit | null> {
        const manifestText = await this.readText(siteTemplateId, version, 'meta/brand-kit.json');
        if (!manifestText) return null;

        let manifest: RawManifest;
        try {
            manifest = JSON.parse(manifestText) as RawManifest;
        } catch {
            return null;
        }

        const stylePath = asPath(manifest.styleUrl);
        const ogPath = asPath(manifest.ogBasePlateUrl);
        const faviconPath = asPath(manifest.faviconUrl);
        const refPaths = Array.isArray(manifest.brandIconReferenceUrls)
            ? manifest.brandIconReferenceUrls.map(asPath).filter((p): p is string => p !== null).slice(0, 8)
            : [];

        const [style, ogBasePlate, favicon, references] = await Promise.all([
            stylePath ? this.readStyle(siteTemplateId, version, stylePath) : Promise.resolve(null),
            ogPath ? this.readImage(siteTemplateId, version, ogPath) : Promise.resolve(null),
            faviconPath ? this.readImage(siteTemplateId, version, faviconPath) : Promise.resolve(null),
            this.readImages(siteTemplateId, version, refPaths),
        ]);

        return {
            templateId: typeof manifest.templateId === 'string' ? manifest.templateId : siteTemplateId,
            version: typeof manifest.version === 'string' ? manifest.version : version,
            style,
            ogBasePlate,
            favicon,
            references,
        };
    }

    private async readImages(
        siteTemplateId: string,
        version: string,
        paths: string[]
    ): Promise<BrandKitImage[]> {
        const results = await Promise.all(
            paths.map((p) => this.readImage(siteTemplateId, version, p))
        );
        return results.filter((img): img is BrandKitImage => img !== null);
    }

    private async readImage(
        siteTemplateId: string,
        version: string,
        path: string
    ): Promise<BrandKitImage | null> {
        const file = await this.readFile(siteTemplateId, version, path);
        if (!file) return null;
        const contentType = file.contentType || guessContentType(path);
        return {
            b64: file.b64,
            contentType,
            dataUrl: `data:${contentType};base64,${file.b64}`,
        };
    }

    private async readStyle(
        siteTemplateId: string,
        version: string,
        path: string
    ): Promise<Record<string, unknown> | null> {
        const text = await this.readText(siteTemplateId, version, path);
        if (!text) return null;
        try {
            const parsed: unknown = JSON.parse(text);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? (parsed as Record<string, unknown>)
                : null;
        } catch {
            return null;
        }
    }

    private async readText(
        siteTemplateId: string,
        version: string,
        path: string
    ): Promise<string | null> {
        const file = await this.readFile(siteTemplateId, version, path);
        return file === null ? null : decodeBase64Utf8(file.b64);
    }

    /** Raw base64 + content type of a template file, or null on any failure. */
    private async readFile(
        siteTemplateId: string,
        version: string,
        path: string
    ): Promise<RawFile | null> {
        try {
            const resp = await this.api.client.call(
                config.getSiteTemplateFile({
                    SiteTemplateId: siteTemplateId,
                    BucketOverride: null,
                    BasePrefixOverride: null,
                    Version: version,
                    Path: path,
                })
            );
            return resp.Result === 'Ok'
                ? { b64: resp.ContentBase64, contentType: resp.ContentType }
                : null;
        } catch {
            return null;
        }
    }
}

/** Normalize a manifest path to a build-relative `Path` (strip a leading slash). */
function asPath(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim().replace(/^\/+/, '');
    return trimmed.length > 0 ? trimmed : null;
}

function decodeBase64Utf8(b64: string): string {
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
}

/** Fallback content type from the file extension when the API omits one. */
function guessContentType(path: string): string {
    const ext = path.split('.').pop()?.toLowerCase() ?? '';
    switch (ext) {
        case 'svg': return 'image/svg+xml';
        case 'png': return 'image/png';
        case 'webp': return 'image/webp';
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        case 'gif': return 'image/gif';
        default: return 'application/octet-stream';
    }
}
